// ============================
// BUNDLE BOUGIE MANAGER - Independent System for Bougie Coffret
// Works with CartStateManager, CartEvents, and CartDOM
// ============================

const cartEvents = ['cart:updated', 'cart:refresh', 'cart:change', 'cart:rendered'];

class BundleBougieManager {
  constructor() {
    this.bundleContainer = document.querySelector('.product-bundle-duo');
    if (!this.bundleContainer) return;
    
    this.blockId = this.bundleContainer.closest('[data-block-id]')?.dataset.blockId || '';
    this.modal = document.querySelector('#BundleDuoModal');
    if (!this.modal) {
      console.error('Modal with ID #BundleDuoModal not found.');
      return;
    }
    
    this.selectedProducts = [null, null]; // 2 slots for products
    this.bundleProducts30ml = null; // For slot 1
    this.bundleProductsBougie = null; // For slot 2
    this.currentSlot = null;
    this.discountPercentage = parseInt(this.bundleContainer.querySelector('.product-bundle-duo__discount')?.textContent.match(/\d+/)?.[0] || 0);
    
    // Cart system integration
    this.cartSubscription = null;
    this.isProcessingBundleCheck = false;
    
    // Bundle product identification
    this.coffretProductId = null;
    this.coffretProductHandle = 'coffret-bougie'; // Update this to match your bougie product handle
    
    // Find the original add to cart button and form
    this.originalAddToCartButton = document.querySelector('[name="add"]') || document.querySelector('.product-form__cart-submit');
    this.productForm = document.querySelector('product-form form') || document.querySelector('form[action*="/cart/add"]');
    
    this.init();
  }

  async init() {
    await this.loadBundleProducts();
    this.setupModal();
    this.setupOriginalButtonIntegration();
    this.setupCartIntegration();
  }

  // ============================
  // CART SYSTEM INTEGRATION
  // ============================
  
  setupCartIntegration() {
    if (window.CartStateManager?.subscribe) {
      this.cartSubscription = window.CartStateManager.subscribe(
        this.handleCartStateChange.bind(this),
        {
          id: 'bundle-bougie-manager',
          priority: 10,
          debounce: false
        }
      );
    }
    
    document.addEventListener('cart:updated', this.handleCartEvent.bind(this));
    document.addEventListener('cart:refresh', this.handleCartEvent.bind(this));
    
    this.setupCheckoutInterceptor();
    this.initializeMainProductId();
  }

  setupCheckoutInterceptor() {
    const checkoutButtonObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.attachCheckoutValidation();
        }
      });
    });

    const cartDrawer = document.querySelector('#CartDrawer, cart-drawer, .cart-drawer');
    const cartForm = document.querySelector('form[action="/cart"]');
    
    if (cartDrawer) {
      checkoutButtonObserver.observe(cartDrawer, { childList: true, subtree: true });
    }
    if (cartForm) {
      checkoutButtonObserver.observe(cartForm, { childList: true, subtree: true });
    }

    this.attachCheckoutValidation();
  }

  attachCheckoutValidation() {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [href="/checkout"], #checkout, .checkout-button, .cart__checkout-button'
    );

    checkoutButtons.forEach(button => {
      if (!button.hasAttribute('data-bundle-bougie-validated')) {
        button.setAttribute('data-bundle-bougie-validated', 'true');
        
        const clickHandler = async (event) => {
          event.preventDefault();
          event.stopPropagation();

          const isValid = await this.validateCartForCheckout();
          
          if (isValid) {
            button.removeEventListener('click', clickHandler);
            
            if (button.tagName === 'A') {
              window.location.href = button.href;
            } else {
              button.form?.submit() || (window.location.href = '/checkout');
            }
            
            setTimeout(() => {
              button.addEventListener('click', clickHandler);
            }, 100);
          }
        };
        
        button.addEventListener('click', clickHandler);
      }
    });
  }

  async validateCartForCheckout() {
    try {
      const cart = await window.CartStateManager?.getCart(true) || await this.getCartDirect();
      
      if (!cart?.items?.length) return true;

      const bundleItems = cart.items.filter(item => 
        item.properties && (
          item.properties['Part of'] === 'Coffret Bougie' ||
          item.properties['Coffret-Bougie-Bundle'] === 'true'
        )
      );

      const mainCoffret = cart.items.find(item => 
        item.product_id === this.coffretProductId || 
        item.handle === this.coffretProductHandle ||
        item.product_handle === this.coffretProductHandle ||
        (item.properties && item.properties.Bundle === 'Coffret Bougie')
      );

      if (bundleItems.length > 0 && !mainCoffret) {
        const message = Shopify.locale === 'fr' 
          ? 'Impossible de procéder au paiement. Nettoyage du panier en cours...'
          : 'Cannot proceed to checkout. Cleaning cart...';
        alert(message);

        await this.cleanupBundleFragrances(bundleItems);
        await this.refreshCartState();
        return false;
      }

      return true;

    } catch (error) {
      console.error('Error validating cart for checkout:', error);
      
      // SECURITY: Don't allow checkout on validation errors
      // Log the error but show a user-friendly message
      const message = Shopify.locale === 'fr' 
        ? 'Erreur lors de la validation du panier. Veuillez actualiser la page et réessayer.'
        : 'Error validating cart. Please refresh the page and try again.';
      alert(message);
      
      return false; // Block checkout on validation error for security
    }
  }

  async getCartDirect() {
    try {
      const response = await fetch('/cart.js');
      if (!response.ok) throw new Error('Failed to fetch cart');
      return await response.json();
    } catch (error) {
      console.error('Error fetching cart directly:', error);
      return null;
    }
  }
  
  async initializeMainProductId() {
    try {
      if (window.CartStateManager?.getCart) {
        const cart = await window.CartStateManager.getCart();
        this.identifyMainProductId(cart);
      }
    } catch (error) {
      console.error('Error initializing main product ID:', error);
    }
  }
  
  identifyMainProductId(cart) {
    if (!cart?.items) return;
    
    const mainProduct = cart.items.find(item => 
      item.handle === this.coffretProductHandle ||
      item.product_handle === this.coffretProductHandle ||
      (item.properties && item.properties.Bundle === 'Coffret Bougie')
    );
    
    if (mainProduct) {
      this.coffretProductId = mainProduct.product_id;
    }
  }
  
  handleCartStateChange(newCart, previousCart) {
    if (this.isProcessingBundleCheck) return;
    
    this.identifyMainProductId(newCart);
    this.checkForBundleCleanup(newCart, previousCart);
  }
  
  handleCartEvent(event) {
    if (this.isProcessingBundleCheck) return;
    
    setTimeout(() => {
      this.checkForBundleCleanupFromEvent();
    }, 100);
  }
  
  async checkForBundleCleanupFromEvent() {
    try {
      if (window.CartStateManager?.getCart) {
        const cart = await window.CartStateManager.getCart(true);
        this.checkForBundleCleanup(cart);
      }
    } catch (error) {
      console.error('Error checking for bundle cleanup:', error);
    }
  }
  
  checkForBundleCleanup(newCart, previousCart = null) {
    if (!newCart?.items) return;
    
    const mainProduct = newCart.items.find(item => 
      item.product_id === this.coffretProductId || 
      item.handle === this.coffretProductHandle ||
      item.product_handle === this.coffretProductHandle ||
      (item.properties && item.properties.Bundle === 'Coffret Bougie')
    );
    
    const bundleFragrances = newCart.items.filter(item => 
      item.properties && (
        item.properties['Part of'] === 'Coffret Bougie' ||
        item.properties['Coffret-Bougie-Bundle'] === 'true'
      )
    );
    
    if (!mainProduct && bundleFragrances.length > 0) {
      if (this.isProcessingBundleCheck) return;
      
      this.cleanupBundleFragrances(bundleFragrances);
      
      setTimeout(() => {
        this.checkForBundleCleanupFromEvent();
      }, 2000);
    }
  }
  
  async cleanupBundleFragrances(bundleFragrances) {
    if (!bundleFragrances || bundleFragrances.length === 0) return;
    
    this.isProcessingBundleCheck = true;
    
    try {
      this.disableCheckoutButtons();
      
      const removalPromises = bundleFragrances.map(item => {
        if (window.CartStateManager?.updateCartItem) {
          return window.CartStateManager.updateCartItem({
            id: item.key,
            quantity: 0
          });
        } else {
          return this.removeCartItemDirect(item);
        }
      });
      
      await Promise.all(removalPromises);
      
      const currentCart = await window.CartStateManager?.getCart(true) || await this.getCartDirect();
      const remainingBundleItems = currentCart?.items?.filter(item => 
        item.properties && (
          item.properties['Part of'] === 'Coffret Bougie' ||
          item.properties['Coffret-Bougie-Bundle'] === 'true'
        )
      ) || [];
      
      if (remainingBundleItems.length > 0) {
        console.warn('Bundle cleanup incomplete, retrying...');
        await this.cleanupBundleFragrances(remainingBundleItems);
      }
      
      await this.refreshCartState();
      
    } catch (error) {
      console.error('Error during bundle cleanup:', error);
    } finally {
      this.isProcessingBundleCheck = false;
      this.enableCheckoutButtons();
    }
  }

  disableCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [href="/checkout"], #checkout, .checkout-button, .cart__checkout-button'
    );
    
    checkoutButtons.forEach(button => {
      button.disabled = true;
      button.style.opacity = '0.5';
      button.style.pointerEvents = 'none';
    });
  }

  enableCheckoutButtons() {
    const checkoutButtons = document.querySelectorAll(
      '[name="checkout"], [href="/checkout"], #checkout, .checkout-button, .cart__checkout-button'
    );
    
    checkoutButtons.forEach(button => {
      button.disabled = false;
      button.style.opacity = '';
      button.style.pointerEvents = '';
    });
  }

  async removeCartItemDirect(item) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: item.key,
          quantity: 0
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to remove item: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      console.error(`Error removing cart item ${item.title}:`, error);
      throw error;
    }
  }

  // ============================
  // ORIGINAL BUTTON INTEGRATION
  // ============================
  
  setupOriginalButtonIntegration() {
    if (!this.originalAddToCartButton || !this.productForm) {
      console.warn('Original add to cart button or form not found');
      return;
    }

    // Always intercept - don't check for specific product handle
    if (!this.originalAddToCartButton.hasAttribute('data-original-text')) {
      this.originalAddToCartButton.setAttribute('data-original-text', this.originalAddToCartButton.textContent);
    }

    this.originalAddToCartButton.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      
      const selectedCount = this.selectedProducts.filter(p => p !== null).length;
      
      if (selectedCount < 2) {
        this.showBundleRequiredMessage(selectedCount);
      } else {
        this.addCompleteBundleToCart();
      }
    });

    this.productForm.addEventListener('submit', (event) => {
      event.preventDefault();
      const selectedCount = this.selectedProducts.filter(p => p !== null).length;
      
      if (selectedCount < 2) {
        this.showBundleRequiredMessage(selectedCount);
      } else {
        this.addCompleteBundleToCart();
      }
    });
  }

  showBundleRequiredMessage(selectedCount) {
    if (!this.originalAddToCartButton) return;
    
    const originalText = this.originalAddToCartButton.textContent;
    const remaining = 2 - selectedCount;
    const message = Shopify.locale === 'fr' 
      ? `Sélectionnez ${remaining} produit${remaining > 1 ? 's' : ''} de plus`
      : `Select ${remaining} more product${remaining > 1 ? 's' : ''}`;
    
    this.originalAddToCartButton.textContent = message;
    this.originalAddToCartButton.disabled = true;
    
    setTimeout(() => {
      this.originalAddToCartButton.textContent = originalText;
      this.originalAddToCartButton.disabled = false;
    }, 2000);
  }

  async addCompleteBundleToCart() {
    if (!this.originalAddToCartButton) return;

    try {
      const originalText = this.originalAddToCartButton.textContent;
      this.originalAddToCartButton.textContent = Shopify.locale === 'fr' ? 'Ajout en cours...' : 'Adding to cart...';
      this.originalAddToCartButton.disabled = true;

      // Show bundle loading overlay
      showBundleLoadingOverlay();

      // Check if cart was empty BEFORE adding
      let cartWasEmpty = false;
      if (window.CartStateManager) {
        const currentCart = await window.CartStateManager.getCart(false);
        cartWasEmpty = !currentCart || currentCart.item_count === 0;
        console.log('[Bundle Bougie] Cart was empty:', cartWasEmpty);
      }

       // Add main product first
       await this.addMainProductToCart();
       console.log('[Bundle Bougie] Main product added');
       
       // Then add selected fragrance products
       await this.addSelectedProductsToCartBatched();
       console.log('[Bundle Bougie] Fragrance products added');

      // Wait a moment for Shopify to process
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Clear selections
      this.clearSelectedProducts();
      
      // Force refresh cart state
      let updatedCart;
      if (window.CartStateManager) {
        updatedCart = await window.CartStateManager.getCart(true);
        console.log('[Bundle Bougie] Cart state refreshed, items:', updatedCart.item_count);
      }
      
      // Success - restore button
      this.originalAddToCartButton.textContent = originalText;
      this.originalAddToCartButton.disabled = false;
      
      // Hide bundle loading overlay
      hideBundleLoadingOverlay();
      
      // Check if iOS
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      
      // If cart was empty, reload to refresh cart state
      if (cartWasEmpty) {
        if (isIOS) {
          // On iOS: reload then show notification instead of auto-opening
          console.log('[Bundle Bougie] iOS empty cart: reloading then showing notification');
          sessionStorage.setItem('showBundleNotificationAfterRefresh', 'true');
        } else {
          // Other platforms: reload then auto-open drawer
          console.log('[Bundle Bougie] Cart was empty, reloading then opening drawer...');
          sessionStorage.setItem('openCartAfterRefresh', 'true');
        }
        window.location.reload();
        return;
      }
      
      // For non-empty cart, use DOM refresh
      await this.openCartDrawer();
      await new Promise(resolve => setTimeout(resolve, 600));
      
      if (window.CartDOM?.refreshCartDrawerFull) {
        await window.CartDOM.refreshCartDrawerFull('bundle-bougie-added', false);
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { source: 'bundle-bougie' }}));
        
        // iOS specific: Force drawer height recalculation after DOM refresh
        await new Promise(resolve => setTimeout(resolve, 100));
        this.fixIOSDrawerHeight();
      } else {
        console.warn('[Bundle Bougie] CartDOM manager not available, fallback to reload');
        window.location.reload();
      }
      
    } catch (error) {
      console.error('Error adding bundle to cart:', error);
      
      // Hide bundle loading overlay
      hideBundleLoadingOverlay();
      
      // Restore button state
      const originalText = this.originalAddToCartButton.getAttribute('data-original-text') || (Shopify.locale === 'fr' ? 'Ajouter au panier' : 'Add to cart');
      this.originalAddToCartButton.textContent = originalText;
      this.originalAddToCartButton.disabled = false;
      
      alert(Shopify.locale === 'fr' ? 'Erreur lors de l\'ajout au panier. Veuillez réessayer.' : 'Error adding to cart. Please try again.');
    }
  }

  async addMainProductToCart() {
    const variantInput = this.productForm.querySelector('[name="id"]');
    if (!variantInput) {
      throw new Error('Variant ID not found');
    }

    const variantId = variantInput.value;
    const quantity = this.productForm.querySelector('[name="quantity"]')?.value || 1;

    const mainProductData = {
      id: variantId,
      quantity: parseInt(quantity),
      properties: {
        'Bundle': 'Coffret Bougie'
      }
    };

    await this.addItemToCartDirect(mainProductData);
  }

  async addSelectedProductsToCartBatched(selectedProducts = null) {
    const products = selectedProducts || this.selectedProducts.filter(p => p !== null);
    if (products.length === 0) {
      return;
    }

    // First, verify all variant IDs by fetching fresh product data
    const verifiedProducts = await Promise.all(
      products.map(product => this.verifyAndGetValidVariant(product))
    );

     // Prepare bundle items with verified variant IDs
     const bundleItems = verifiedProducts.map((product, index) => ({
       id: product.validVariantId,
       quantity: 1,
       properties: { 
         'Part of': 'Coffret Bougie',
         'Coffret-Bougie-Bundle': 'true',
         '_bundle_item': 'true',
         'Slot': index === 0 ? '30ml' : 'bougie'
       },
       title: product.title
     }));

    // Add items sequentially with delay to avoid rate limiting
    for (const item of bundleItems) {
      try {
        await this.addItemToCartDirect(item);
        // Small delay between requests to avoid rate limiting
        await new Promise(resolve => setTimeout(resolve, 200));
      } catch (error) {
        console.error(`Failed to add ${item.title} to cart:`, error);
        throw error; // Re-throw to stop the process
      }
    }
  }

   async addItemToCartDirect(item) {
     const response = await fetch('/cart/add.js', {
       method: 'POST',
       headers: {
         'Content-Type': 'application/json',
       },
       body: JSON.stringify(item)
     });

     if (!response.ok) {
       const errorData = await response.json().catch(() => ({}));
       console.error('Cart API Error Response:', {
         status: response.status,
         statusText: response.statusText,
         errorData: errorData,
         sentItem: item
       });
       throw new Error(errorData.description || `Failed to add item to cart: ${response.statusText}`);
     }

     return await response.json();
   }

  async refreshCartState() {
    try {
      if (window.CartStateManager) {
        await window.CartStateManager.getCart(true);
      }
      
      if (window.CartQuantity && typeof window.CartQuantity.refreshCartDrawer === 'function') {
        const cart = window.CartStateManager ? await window.CartStateManager.getCart(false) : null;
        await window.CartQuantity.refreshCartDrawer(cart);
        await new Promise(resolve => setTimeout(resolve, 200));
      }
      
      if (window.CartDOM && typeof window.CartDOM.invalidate === 'function') {
        window.CartDOM.invalidate('bundle-bougie-add');
      }
      
      cartEvents.forEach(eventName => {
        document.dispatchEvent(new CustomEvent(eventName, { 
          bubbles: true,
          detail: { source: 'bundle-bougie-add' }
        }));
      });
      
    } catch (error) {
      console.error('[Bundle Bougie] Error refreshing cart state:', error);
    }
  }

  async waitForCartDrawerUpdate() {
    return new Promise((resolve) => {
      const handleDOMReplaced = () => {
        setTimeout(() => {
          resolve();
        }, 100);
        document.removeEventListener('cart:dom-replaced', handleDOMReplaced);
      };
      
      document.addEventListener('cart:dom-replaced', handleDOMReplaced);
      
      setTimeout(() => {
        document.removeEventListener('cart:dom-replaced', handleDOMReplaced);
        resolve();
      }, 1000);
      
      setTimeout(() => {
        if (document.querySelector('#CartDrawer-CartItems .cart-item-modern')) {
          document.removeEventListener('cart:dom-replaced', handleDOMReplaced);
          resolve();
        }
      }, 300);
    });
  }

   async verifyAndGetValidVariant(product) {
     try {
       const response = await fetch(`/products/${product.handle}.js`);
       if (!response.ok) {
         throw new Error(`Failed to fetch product data for ${product.handle}`);
       }
       
       const freshProductData = await response.json();
       
       const variants = freshProductData.variants || [];
       let selectedVariant = null;
       
       if (product._desiredVariantMatch) {
         // For 30ml slot, look for "30ml bundle" variant specifically
         if (product._desiredVariantMatch === '30') {
           // Must contain BOTH "30ml" AND "bundle" (strict match)
           selectedVariant = variants.find(v => {
             const title = String(v.title).toLowerCase();
             return /30\s*ml/i.test(title) && /bundle/i.test(title);
           });
         } else {
           // For bougie slot, look for "bundle_bougie" variant
           selectedVariant = variants.find(v => 
             /bundle_bougie/i.test(String(v.title)) || 
             /bundle\s*bougie/i.test(String(v.title))
           );
         }
       }
       
       // NO FALLBACK for bundle variants - we MUST have the bundle variant
       if (!selectedVariant && product._desiredVariantMatch) {
         throw new Error(`Bundle variant not found for ${product.title}. Please create a variant with the required bundle identifier.`);
       }
       
       if (!selectedVariant) {
         throw new Error(`No variants found for product ${product.title}`);
       }
       
       return {
         ...product,
         validVariantId: selectedVariant.id,
         selectedVariantTitle: selectedVariant.title,
         freshData: freshProductData
       };
       
     } catch (error) {
       console.error(`Error verifying product ${product.title}:`, error);
       throw error;
     }
   }

  // ============================
  // MODAL FUNCTIONALITY
  // ============================

  async loadBundleProducts() {
    try {
      // Prefer scoped lookup within this block; fallback to global with block id
      const dataContainer = this.bundleContainer.querySelector('.bundle-duo-products-container') || document;
      let dataScript30ml = dataContainer.querySelector(`#bundle-duo-products-30ml-${this.blockId}`) ||
                           dataContainer.querySelector('[id^="bundle-duo-products-30ml-"]');
      let dataScriptBougie = dataContainer.querySelector(`#bundle-duo-products-bougie-${this.blockId}`) ||
                            dataContainer.querySelector('[id^="bundle-duo-products-bougie-"]');
      
      if (!dataScript30ml || !dataScriptBougie) {
        console.error('Bundle bougie products data scripts not found.');
        return;
      }

      this.bundleProducts30ml = JSON.parse(dataScript30ml.textContent);
      this.bundleProductsBougie = JSON.parse(dataScriptBougie.textContent);

    } catch (error) {
      console.error('Error loading bundle bougie products data:', error);
    }
  }

  setupModal() {
    this.bundleContainer.querySelectorAll('.product-bundle-duo__add').forEach((button, index) => {
      button.addEventListener('click', () => this.openModal(index));
    });

    this.modal.querySelector('.bundle-duo-modal-close')?.addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.bundle-duo-modal-back')?.addEventListener('click', () => this.closeModal());
    this.modal.querySelector('.bundle-duo-overlay')?.addEventListener('click', () => this.closeModal());

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.modal.classList.contains('active')) {
        this.closeModal();
      }
    });
  }

  openModal(slotIndex) {
    this.currentSlot = slotIndex;
    this.renderProducts();
    this.modal.classList.add('active');
    document.body.style.overflow = 'hidden';
  }

  closeModal() {
    this.modal.classList.remove('active');
    document.body.style.overflow = '';
    this.currentSlot = null;
  }

  renderProducts() {
    const container = this.modal.querySelector('.bundle-duo-options-container');
    
    // Choose the right product set based on slot
    const productsData = this.currentSlot === 0 ? this.bundleProducts30ml : this.bundleProductsBougie;
    
    if (!productsData || productsData.length === 0) {
      container.innerHTML = '<div class="bundle-empty">Aucun produit disponible pour le bundle.</div>';
      return;
    }

    const productsGrid = document.createElement('div');
    productsGrid.className = 'bundle-duo-products-grid';

    productsData.forEach(product => {
      const productCard = this.createProductCard(product);
      productsGrid.appendChild(productCard);
    });

    container.innerHTML = '';
    container.appendChild(productsGrid);
  }

  createProductCard(product) {
    const card = document.createElement('div');
    card.className = 'bundle-duo-product-card';
    
    // Vérifier si le produit est sélectionné dans n'importe quel slot
    const isSelectedInCurrentSlot = this.selectedProducts[this.currentSlot]?.id === product.id;
    const isSelectedInAnySlot = this.selectedProducts.some(selectedProduct => selectedProduct?.id === product.id);
    
    // For bougie slot, show product name + variant type
    let titleContent = product.title;
    let descriptionContent = product.subdescription || product.description;
    
    if (this.currentSlot === 1) {
      // For bougie products, product.title now contains the source product name
      // We just need to add "- Bougie" to indicate the variant type
      const productName = product.title;
      titleContent = `${productName} - Bougie`;
      descriptionContent = product.subdescription || product.description;
    }

    card.innerHTML = `
      <div class="bundle-duo-product-card__inner">
        <div class="bundle-duo-product-card__image">
          <img src="${product.image}" alt="${product.title}" loading="lazy">
          ${isSelectedInAnySlot ? `<span class="bundle-duo-product-card__selected-badge">${Shopify.locale === 'fr' ? 'Sélectionné' : 'Selected'}</span>` : ''}
        </div>
        <div class="bundle-duo-product-card__content-text">
          <h3 class="bundle-duo-product-card__title">${titleContent}</h3>
          <p class="bundle-duo-product-card__description">${descriptionContent}</p>
        </div>
        <button class="bundle-duo-product-card__button ${isSelectedInCurrentSlot ? 'bundle-duo-product-card__button--selected' : ''}" 
                data-product-id="${product.id}">
          ${isSelectedInCurrentSlot ? (Shopify.locale === 'fr' ? 'Sélectionné' : 'Selected') : (Shopify.locale === 'fr' ? 'Sélectionner' : 'Select')}
        </button>
      </div>
    `;

    card.querySelector('.bundle-duo-product-card__button').addEventListener('click', () => {
      this.selectProduct(product);
    });

    return card;
  }

  selectProduct(product) {
    const enrichedProduct = { ...product };
    enrichedProduct._duoSlot = this.currentSlot;
    if (this.currentSlot === 1) {
      enrichedProduct._desiredVariantMatch = 'bougie';
    } else {
      enrichedProduct._desiredVariantMatch = '30';
    }
    this.selectedProducts[this.currentSlot] = enrichedProduct;
    this.updateBundleDisplay();
    this.closeModal();
  }

  updateBundleDisplay() {
    this.bundleContainer.querySelectorAll('.product-bundle-duo__item').forEach((item, index) => {
      const product = this.selectedProducts[index];
      const wrapper = item.querySelector('.product-bundle-duo__add-wrapper');

      if (product) {
        // For bougie slot, show product name + variant type
        let displayTitle = product.title;
        
        if (index === 1) {
          // For bougie products, product.title now contains the source product name
          // We just need to add "- Bougie" to indicate the variant type
          const productName = product.title;
          displayTitle = `${productName} - Bougie`;
        }

        wrapper.innerHTML = `
          <div class="product-bundle-duo__item-content">
            <div class="product-bundle-duo__image">
              <img src="${product.image}" alt="${product.title}">
            </div>
            <div class="product-bundle-duo__details">
              <h4 class="product-bundle-duo__title">${displayTitle}</h4>
              <div class="product-bundle-duo__desc">${product.subdescription || product.description || ''}</div>
              <div class="product-bundle-duo__price">0€</div>
            </div>
            <button type="button" class="product-bundle-duo__edit" data-index="${index}">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M2 11.5V14h2.5L12.5 6L10 3.5L2 11.5z" stroke="currentColor" stroke-width="1.5"/>
              </svg>
            </button>
          </div>
        `;

        wrapper.querySelector('.product-bundle-duo__edit').addEventListener('click', () => {
          this.openModal(index);
        });
      }
    });

    this.updateBundleTotal();
    this.showAddToCartButton();
  }

  updateBundleTotal() {
    const selectedCount = this.selectedProducts.filter(p => p !== null).length;
    const totalContainer = this.bundleContainer.querySelector('.product-bundle-duo__total');
    
    if (selectedCount > 0) {
      totalContainer.style.display = 'none';
      totalContainer.querySelector('.product-bundle-duo__total-price').textContent = '0€';
    } else {
      totalContainer.style.display = 'none';
    }
  }

  showAddToCartButton() {
    const selectedCount = this.selectedProducts.filter(p => p !== null).length;
    
    if (this.originalAddToCartButton) {
      if (selectedCount === 2) {
        this.originalAddToCartButton.textContent = Shopify.locale === 'fr' ? 'Ajouter le coffret au panier' : 'Add the set to cart';
      } else {
        const remaining = 2 - selectedCount;
        this.originalAddToCartButton.textContent = Shopify.locale === 'fr' 
          ? `Sélectionnez ${remaining} produit${remaining > 1 ? 's' : ''} de plus`
          : `Select ${remaining} more product${remaining > 1 ? 's' : ''}`;
      }
    }

    const existingButton = this.bundleContainer.querySelector('.bundle-duo-add-to-cart');
    if (existingButton) {
      existingButton.remove();
    }
  }

  clearSelectedProducts() {
    this.selectedProducts = [null, null];
    this.updateBundleDisplay();
  }

  // Open cart drawer using your cart system methods
  async openCartDrawer() {
    // Method 1: Try cart drawer toggle button (most reliable)
    const cartToggle = document.querySelector('cart-drawer-items summary, .header__icon--cart, [data-cart-toggle], .cart-count-bubble');
    if (cartToggle) {
      cartToggle.click();
      return;
    }
    
    // Method 2: Try direct cart drawer manipulation
    const cartDrawer = document.querySelector('cart-drawer, #CartDrawer');
    if (cartDrawer) {
      // Try the open method if available
      if (typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        return;
      }
      
      // Fallback: manipulate attributes and classes
      cartDrawer.setAttribute('open', '');
      cartDrawer.classList.add('active', 'open', 'is-open');
      
      // Show overlay if it exists
      const overlay = document.querySelector('#CartDrawer-Overlay, .drawer-overlay');
      if (overlay) {
        overlay.style.display = 'block';
      }
      return;
    }
    
    // Method 3: Dispatch cart open events
    const openEvents = ['cart:open', 'cart-drawer:open', 'drawerOpen'];
    openEvents.forEach(eventName => {
      document.dispatchEvent(new CustomEvent(eventName, { 
        bubbles: true,
        detail: { source: 'bundle-bougie-add' }
      }));
    });
  }

  // Fix iOS drawer height issues after bundle addition
  fixIOSDrawerHeight() {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (!isIOS) return;

    console.log('[Bundle Bougie] Fixing iOS drawer height...');
    
    const cartDrawer = document.querySelector('cart-drawer');
    const drawerInner = document.querySelector('.drawer__inner');
    
    if (!cartDrawer || !drawerInner) {
      console.warn('[Bundle Bougie] Drawer elements not found for iOS fix');
      return;
    }

    // Force reflow and recalculation
    const currentHeight = window.innerHeight;
    const visualViewportHeight = window.visualViewport ? window.visualViewport.height : currentHeight;
    
    console.log('[Bundle Bougie] iOS heights - inner:', currentHeight, 'visual:', visualViewportHeight);
    
    // Force drawer to recalculate its height
    drawerInner.style.height = `${visualViewportHeight}px`;
    
    // Trigger a reflow
    void drawerInner.offsetHeight;
    
    // Reset to dvh (will use CSS value)
    setTimeout(() => {
      drawerInner.style.height = '';
      console.log('[Bundle Bougie] iOS drawer height fixed');
    }, 50);
  }

  cleanup() {
    if (this.cartSubscription) {
      this.cartSubscription();
      this.cartSubscription = null;
    }
    
    document.removeEventListener('cart:updated', this.handleCartEvent);
    document.removeEventListener('cart:refresh', this.handleCartEvent);
  }
}

// ============================
// GLOBAL INITIALIZATION
// ============================

function initBundleBougieManager() {
  // Instantiate immediately so modal works even if cart system isn't ready yet
  const bundleBougieManager = new BundleBougieManager();
  window.bundleBougieManager = bundleBougieManager;

  // Provide a helper for manual cleanup checks (optional)
  window.cleanupOrphanedBundleBougieItems = function() {
    if (window.bundleBougieManager) {
      return window.bundleBougieManager.checkForBundleCleanupFromEvent();
    }
    console.warn('Bundle bougie manager not available');
  };
}

// Check if cart drawer should open or notification should show after page refresh
function checkOpenCartAfterRefresh() {
  // Check if we should show notification instead (iOS only)
  if (sessionStorage.getItem('showBundleNotificationAfterRefresh') === 'true') {
    sessionStorage.removeItem('showBundleNotificationAfterRefresh');
    
    // Wait for page to fully load, then show notification
    setTimeout(() => {
      console.log('[Bundle Bougie] Showing success notification after refresh');
      showBundleSuccessNotification();
    }, 500);
    return;
  }
  
  // Normal behavior: open cart drawer
  if (sessionStorage.getItem('openCartAfterRefresh') === 'true') {
    
    // Clear the flag
    sessionStorage.removeItem('openCartAfterRefresh');
    
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const initialDelay = isIOS ? 800 : 500; // Longer delay for iOS
    
    // Wait for page to fully load, then open cart drawer
    setTimeout(() => {
      // Wait for CartDOM to be ready on iOS
      if (isIOS && window.CartDOM) {
        window.CartDOM.invalidate('page-reload');
      }
      
      // Try multiple methods to open cart drawer
      const cartToggle = document.querySelector('cart-drawer-items summary, .header__icon--cart, [data-cart-toggle], .cart-count-bubble');
      if (cartToggle) {
        cartToggle.click();
        
        // iOS: Fix drawer height after opening with longer delay
        setTimeout(() => {
          fixIOSDrawerHeightGlobal();
          forceCartDrawerScrollTop();
          forceCartItemsLayoutRecalc();
          stabilizeCartDrawerIOS();
          
          // Additional height fix after a moment
          if (isIOS) {
            setTimeout(() => {
              fixIOSDrawerHeightGlobal();
              forceCartDrawerScrollTop();
              forceCartItemsLayoutRecalc();
            }, 500);
          }
        }, isIOS ? 600 : 300);
        return;
      }
      
      // Fallback: try direct cart drawer manipulation
      const cartDrawer = document.querySelector('cart-drawer, #CartDrawer');
      if (cartDrawer && typeof cartDrawer.open === 'function') {
        cartDrawer.open();
        
        // iOS: Fix drawer height after opening with longer delay
        setTimeout(() => {
          fixIOSDrawerHeightGlobal();
          forceCartDrawerScrollTop();
          forceCartItemsLayoutRecalc();
          stabilizeCartDrawerIOS();
          
          // Additional height fix after a moment
          if (isIOS) {
            setTimeout(() => {
              fixIOSDrawerHeightGlobal();
              forceCartDrawerScrollTop();
              forceCartItemsLayoutRecalc();
            }, 500);
          }
        }, isIOS ? 600 : 300);
        return;
      }
      
      // Fallback: dispatch events
      document.dispatchEvent(new CustomEvent('cart:open', { bubbles: true }));
      
      // iOS: Fix drawer height after opening with longer delay
      setTimeout(() => {
        fixIOSDrawerHeightGlobal();
        forceCartDrawerScrollTop();
        forceCartItemsLayoutRecalc();
        stabilizeCartDrawerIOS();
        
        // Additional height fix after a moment
        if (isIOS) {
          setTimeout(() => {
            fixIOSDrawerHeightGlobal();
            forceCartDrawerScrollTop();
            forceCartItemsLayoutRecalc();
          }, 500);
        }
      }, isIOS ? 600 : 300);
      
    }, initialDelay);
  }
}

// Force scroll to top of cart drawer (helps with iOS height calculation)
function forceCartDrawerScrollTop() {
  const drawerInner = document.querySelector('.drawer__inner');
  const cartItems = document.querySelector('#CartDrawer-CartItems');
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  
  if (drawerInner) {
    drawerInner.scrollTop = 0;
  }
  if (cartItems) {
    cartItems.scrollTop = 0;
  }
  if (cartDrawerItems) {
    cartDrawerItems.scrollTop = 0;
  }
}

// Force recalculation of cart drawer items container layout on iOS
function forceCartItemsLayoutRecalc() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;
  
  console.log('[Bundle Bougie] Forcing cart items layout recalculation...');
  
  const cartDrawerItems = document.querySelector('cart-drawer-items');
  const drawerContents = document.querySelector('.drawer__contents');
  const drawerFooter = document.querySelector('.drawer__footer, .cart-footer');
  
  if (!cartDrawerItems) {
    console.warn('[Bundle Bougie] cart-drawer-items not found for layout fix');
    return;
  }
  
  // Force flexbox recalculation by temporarily removing and restoring flex
  const originalFlex = cartDrawerItems.style.flex;
  cartDrawerItems.style.flex = 'none';
  
  // Force reflow
  void cartDrawerItems.offsetHeight;
  
  // Restore flex
  cartDrawerItems.style.flex = originalFlex || '';
  
  // Also force reflow on parent and footer
  if (drawerContents) {
    void drawerContents.offsetHeight;
  }
  if (drawerFooter) {
    void drawerFooter.offsetHeight;
  }
  
  // Trigger one more reflow
  void cartDrawerItems.offsetHeight;
  
  console.log('[Bundle Bougie] Cart items layout recalculation complete');
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initBundleBougieManager();
    checkOpenCartAfterRefresh();
  });
} else {
  initBundleBougieManager();
  checkOpenCartAfterRefresh();
}

// ============================
// BUNDLE LOADING OVERLAY
// ============================

function showBundleLoadingOverlay() {
  // Check if overlay already exists
  let overlay = document.getElementById('bundle-loading-overlay');
  if (overlay) {
    overlay.style.display = 'flex';
    return;
  }

  // Create overlay
  overlay = document.createElement('div');
  overlay.id = 'bundle-loading-overlay';
  overlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 99999;
    backdrop-filter: blur(4px);
  `;

  // Create content container
  const content = document.createElement('div');
  content.style.cssText = `
    background: white;
    border-radius: 12px;
    padding: 40px;
    text-align: center;
    max-width: 400px;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  `;

  // Create spinner
  const spinner = document.createElement('div');
  spinner.style.cssText = `
    width: 50px;
    height: 50px;
    border: 4px solid #f3f3f3;
    border-top: 4px solid #1f1d1a;
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 20px;
  `;

  // Create text
  const text = document.createElement('p');
  text.style.cssText = `
    font-size: 18px;
    font-weight: 500;
    color: #1f1d1a;
    margin: 0;
    line-height: 1.5;
  `;
  const isEnglish = Shopify.locale === 'en';
  text.textContent = isEnglish ? 'Composing your set...' : 'Composition du coffret en cours...';

  // Add spinner animation if not exists
  if (!document.getElementById('bundle-spinner-animation')) {
    const style = document.createElement('style');
    style.id = 'bundle-spinner-animation';
    style.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  // Assemble
  content.appendChild(spinner);
  content.appendChild(text);
  overlay.appendChild(content);
  document.body.appendChild(overlay);
}

function hideBundleLoadingOverlay() {
  const overlay = document.getElementById('bundle-loading-overlay');
  if (overlay) {
    overlay.style.display = 'none';
    // Don't remove, just hide for potential reuse
  }
}

// ============================
// BUNDLE SUCCESS NOTIFICATION
// ============================

function showBundleSuccessNotification() {
  const isEnglish = typeof Shopify !== 'undefined' && Shopify.locale === 'en';
  
  // Check if notification already exists
  let existingNotif = document.getElementById('bundle-success-notification');
  if (existingNotif) {
    existingNotif.remove();
  }
  
  // Create notification
  const notification = document.createElement('div');
  notification.id = 'bundle-success-notification';
  notification.style.cssText = `
    position: fixed;
    top: 20px;
    right: 20px;
    background: #1f1d1a;
    color: white;
    padding: 12px 16px;
    border-radius: 6px;
    z-index: 10000;
    box-shadow: 0 4px 12px rgba(0,0,0,0.3);
    display: flex;
    align-items: center;
    gap: 10px;
    font-family: "Work Sans", sans-serif;
    animation: slideInRight 0.3s ease-out;
    max-width: 320px;
  `;
  
  // Add animation
  if (!document.getElementById('bundle-notification-animation')) {
    const style = document.createElement('style');
    style.id = 'bundle-notification-animation';
    style.textContent = `
      @keyframes slideInRight {
        from {
          transform: translateX(400px);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOutRight {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(400px);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }
  
  // Create content
  const checkmark = document.createElement('span');
  checkmark.textContent = '✓';
  checkmark.style.cssText = `
    font-size: 18px;
    font-weight: bold;
  `;
  
  const message = document.createElement('span');
  message.textContent = isEnglish ? 'Set added to cart' : 'Coffret ajouté au panier';
  message.style.cssText = `
    flex: 1;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.4;
  `;
  
  const button = document.createElement('button');
  button.textContent = isEnglish ? 'View' : 'Voir';
  button.style.cssText = `
    background: white;
    color: #1f1d1a;
    border: none;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    font-weight: 500;
    font-family: "Work Sans", sans-serif;
    transition: all 0.2s ease;
    white-space: nowrap;
  `;
  
  button.onmouseover = () => {
    button.style.background = '#f5f5f5';
  };
  button.onmouseout = () => {
    button.style.background = 'white';
  };
  
  button.onclick = () => {
    const cartToggle = document.querySelector('.header__icon--cart, [data-cart-toggle], cart-drawer-items summary');
    if (cartToggle) {
      cartToggle.click();
    }
    removeNotification();
  };
  
  // Assemble
  notification.appendChild(checkmark);
  notification.appendChild(message);
  notification.appendChild(button);
  document.body.appendChild(notification);
  
  // Auto-hide after 5 seconds
  setTimeout(() => {
    removeNotification();
  }, 5000);
  
  function removeNotification() {
    if (notification && notification.parentElement) {
      notification.style.animation = 'slideOutRight 0.3s ease-out';
      setTimeout(() => {
        if (notification.parentElement) {
          notification.remove();
        }
      }, 300);
    }
  }
}

// ============================
// iOS CART DRAWER STABILIZATION
// ============================

function isIOSDevice() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

// Global function to fix iOS drawer height (callable from checkOpenCartAfterRefresh)
function fixIOSDrawerHeightGlobal() {
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  if (!isIOS) return;

  console.log('[Bundle Bougie Global] Fixing iOS drawer height...');
  
  const cartDrawer = document.querySelector('cart-drawer');
  const drawerInner = document.querySelector('.drawer__inner');
  const cartItems = document.querySelector('#CartDrawer-CartItems');
  
  if (!cartDrawer || !drawerInner) {
    console.warn('[Bundle Bougie Global] Drawer elements not found for iOS fix');
    return;
  }

  // Force reflow and recalculation
  const currentHeight = window.innerHeight;
  const visualViewportHeight = window.visualViewport ? window.visualViewport.height : currentHeight;
  
  console.log('[Bundle Bougie Global] iOS heights - inner:', currentHeight, 'visual:', visualViewportHeight);
  
  // Remove any inline height first to reset
  drawerInner.style.height = '';
  
  // Force reflow
  void drawerInner.offsetHeight;
  
  // Set explicit height
  drawerInner.style.height = `${visualViewportHeight}px`;
  
  // Also fix cart items container if it exists
  if (cartItems) {
    cartItems.style.minHeight = 'auto';
    void cartItems.offsetHeight;
  }
  
  // Trigger another reflow
  void drawerInner.offsetHeight;
  
  // Reset to dvh (will use CSS value) after a moment
  setTimeout(() => {
    drawerInner.style.height = '';
    console.log('[Bundle Bougie Global] iOS drawer height fixed');
    
    // Force one final reflow
    void drawerInner.offsetHeight;
  }, 100);
}

function stabilizeCartDrawerIOS() {
  if (!isIOSDevice()) return;
  try {
    // Single adjustment - simplified approach
    window.CartSafariViewport?.forceAdjustment?.();
  } catch (e) {}
}
